/**
 * Mise à jour optimiste — patcher le cache avant la réponse serveur.
 *
 * Sans ça, chaque changement de statut (confirmer un rendez-vous, encaisser,
 * marquer une présence) laisse l'interface figée le temps d'un aller-retour.
 * Sur une connexion guinéenne, ce délai est ressenti comme de la lenteur alors
 * que le serveur répond correctement.
 *
 * Le helper patche indistinctement :
 *  - les réponses paginées DRF (`{ results: [...] }`),
 *  - les listes nues,
 *  - les détails (objet unique portant l'`id` visé),
 * pour toutes les requêtes en cache sous la clé racine de la feature.
 *
 * En cas d'échec serveur, `rollbackOptimistic` restaure l'instantané : l'écran
 * revient à l'état exact d'avant, et le `onError` existant affiche l'erreur.
 */

import type { QueryClient } from "@tanstack/react-query";

/** Instantané du cache avant patch, à rejouer en cas d'échec. */
export interface OptimisticSnapshot {
  entries: Array<[readonly unknown[], unknown]>;
}

interface Identifiable {
  id: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasId(value: unknown, id: string): value is Identifiable {
  return isRecord(value) && value.id === id;
}

/** Applique `patch` à l'entité `id` partout où elle apparaît dans `data`. */
function patchCacheEntry<T extends Identifiable>(
  data: unknown,
  id: string,
  patch: (entity: T) => T,
): unknown {
  if (Array.isArray(data)) {
    return data.map((item) => (hasId(item, id) ? patch(item as T) : item));
  }

  if (isRecord(data)) {
    // Réponse paginée DRF.
    if (Array.isArray(data.results)) {
      return { ...data, results: patchCacheEntry(data.results, id, patch) };
    }
    // Liste paginée infinie (`useInfiniteQuery`).
    if (Array.isArray(data.pages)) {
      return {
        ...data,
        pages: data.pages.map((page) => patchCacheEntry(page, id, patch)),
      };
    }
    // Détail.
    if (hasId(data, id)) {
      return patch(data as T);
    }
  }

  return data;
}

/**
 * Patche le cache immédiatement et retourne l'instantané de restauration.
 *
 * À appeler depuis `onMutate`. Annule d'abord les requêtes en vol : sans ça,
 * une réponse serveur périmée arrivée après le patch écraserait l'optimisme.
 */
export async function applyOptimistic<T extends Identifiable>(
  qc: QueryClient,
  rootKey: readonly unknown[],
  id: string,
  patch: (entity: T) => T,
): Promise<OptimisticSnapshot> {
  await qc.cancelQueries({ queryKey: rootKey });

  const entries = qc.getQueriesData({ queryKey: rootKey });
  const snapshot: OptimisticSnapshot = { entries: [...entries] };

  for (const [queryKey, data] of entries) {
    if (data === undefined) continue;
    qc.setQueryData(queryKey, patchCacheEntry(data, id, patch));
  }

  return snapshot;
}

/** Restaure l'instantané pris par `applyOptimistic`. À appeler depuis `onError`. */
export function rollbackOptimistic(
  qc: QueryClient,
  snapshot: OptimisticSnapshot | undefined,
): void {
  if (!snapshot) return;
  for (const [queryKey, data] of snapshot.entries) {
    qc.setQueryData(queryKey, data);
  }
}
